import type { ReactNode } from 'react';
import { Code, CodeSample } from '../CodeSample';
import { verifySignature } from '../samples';
import { GuideHeader } from './GuideHeader';
import { addWebhook, delivery, testWebhook } from './webhookData';

/** A webhook destination: adding one, what it receives, checking the signature, the delivery rules. */
export function WebhooksGuide(): ReactNode {
  return (
    <article className="docs-page">
      <GuideHeader slug="webhooks" />
      <Add />
      <Receives />
      <Verify />
      <Rules />
    </article>
  );
}

function Add(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="add-title">
      <h2 id="add-title">Add a webhook to a job</h2>
      <p>
        A webhook is one of a job’s destinations. Give it your endpoint (https, reachable from the
        internet) and a secret of your own, at least 16 characters, that Paige signs every post
        with. The answer never shows the secret again; it says <code>secretSet: true</code>.
      </p>
      <Code code={addWebhook} label="the call that adds a webhook" />
      <p>
        Then try it: Paige posts a signed <code>test</code> event,{' '}
        <code>{'{"event": "test", "job_id", "destination", "at"}'}</code>, and says what your
        endpoint answered.
      </p>
      <Code code={testWebhook} label="the call that tests a webhook" />
    </section>
  );
}

function Receives(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="receives-title">
      <h2 id="receives-title">What your endpoint receives</h2>
      <p>
        One <code>POST</code> for each document revision the job confirms, as it is exported. The
        body is the revision’s <code>document.json</code> with the links to its package files, which
        you fetch with your API key. Three headers come with it: <code>Paige-Event</code>,{' '}
        <code>Paige-Delivery</code> (the delivery’s id) and <code>Paige-Signature</code>.
      </p>
      <Code code={delivery} label="a delivery" />
    </section>
  );
}

function Verify(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="verify-title">
      <h2 id="verify-title">Check the signature</h2>
      <p>
        <code>Paige-Signature: t=&lt;unix seconds&gt;,v1=&lt;hex&gt;</code>, where <code>v1</code>{' '}
        is HMAC-SHA256, keyed with your secret, over <code>t</code>, a period, and the raw body.
        Check it over the bytes exactly as they arrived, before parsing them, compare in constant
        time, and refuse a <code>t</code> more than five minutes from now. Answer 401 to a post that
        fails.
      </p>
      <CodeSample code={verifySignature} label="Checking a delivery's signature" />
    </section>
  );
}

function Rules(): ReactNode {
  return (
    <section className="docs-section" aria-labelledby="rules-title">
      <h2 id="rules-title">How deliveries behave</h2>
      <ul className="docs-list">
        <li>
          <strong>At least once.</strong> A post can arrive twice: <code>Paige-Delivery</code> is
          the same on every attempt of one revision to one webhook (it is the body’s{' '}
          <code>delivery_id</code> without its dashes), so keep the ids you have handled and answer
          200 to one you have seen.
        </li>
        <li>
          <strong>Answer quickly.</strong> Any 2xx is received. Do slow work after answering.
        </li>
        <li>
          <strong>Retries.</strong> Any other answer, or none, is tried again with backoff, five
          attempts in all. Then the delivery has failed, the destination’s <code>health</code> is{' '}
          <code>down</code> and its <code>lastError</code> says what your endpoint answered; fix it
          and <code>POST .../retry</code> sends what failed. 401 and 403 are taken as your endpoint
          refusing the signature.
        </li>
        <li>
          <strong>Order.</strong> Posts are not ordered across documents; a later revision of one
          document has a higher <code>revision</code>.
        </li>
      </ul>
    </section>
  );
}
